import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import base64 from "base64-js";
import {
  Container, Typography, Paper, Select, FormControl, Box, MenuItem, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions, styled, Button,
  InputLabel, Table, TableBody, TableCell, TableHead, TableRow, Radio,
  RadioGroup, FormControlLabel, TextField, Modal, Tooltip
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function ExamCategory() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [canSave, setCanSave] = useState(false);
  const { encodedFormId } = useParams();
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [className, setClassName] = useState([]);
  const [examData, setExamData] = useState([]);
  const [formData, setFormData] = useState({
    classTypeId: "",
    examType: "",
    isExamModeIndependent: true,
    isExamWeightIndependent: true,
    minMarks: "",
    maxMarks: ""
  });
  const [selectedExam, setSelectedExam] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const navigate = useNavigate();

  const decodeFormId = (encodedFormId) => {
    try {
      const bytes = base64.toByteArray(encodedFormId);
      return new TextDecoder().decode(bytes);
    } catch (error) {
      console.error("Error decoding formId:", error);
      return "";
    }
  };

  const encodeFormId = (formId) => {
    try {
      const bytes = new TextEncoder().encode(formId);
      return base64.fromByteArray(bytes);
    } catch (error) {
      console.error("Error encoding formId:", error);
      return "";
    }
  };

  const formId = decodeFormId(encodedFormId);

  const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 20,
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  }));

  const fetchClassddl = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ClassType/GetClassType`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({}),
      });
      if (response.ok) {
        const responseData = await response.json();
        if (responseData.status !== null) {
          setClassName(responseData);
        } else {
          console.error('No data found for classes');
        }
        if (responseData.msg && responseData.msg !== 'Record Not Found') {
          console.error('API error:', responseData.msg);
        }
      } else {
        console.error('Failed to fetch class data');
      }
    } catch (error) {
      console.error('API request error:', error);
    }
  };

  const Authorizer = async () => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/CPanel/Module_Authorizer`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          formId,
        }),
      });
      if (response.ok) {
        const responseData = await response.json();
        const authorizationData = responseData[0];
        setCanDelete(authorizationData.uDelete === 1);
        setCanEdit(authorizationData.uModify === 1);
        setCanSave(authorizationData.uWrite === 1);
      } else {
        console.error("Authorization failed:", response.status);
        // alert("Invalid authorization response");
        setSnackbarMessage('Invalid authorization response');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }
    } catch (error) {
      console.error("API request error:", error);
      // alert("An error occurred. Please try again later.");
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleRadioChange = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value === "true"
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!formData.classTypeId || !formData.examType) {
      // alert('Please select Class Type and enter Exam Type.');
      setSnackbarMessage('Please select Class Type and enter Exam Type.');
      setOpenSnackbar(true);
      setSnackbarSeverity("warning")
      return;
    }
    if (formData.isExamModeIndependent) {
      if (!formData.minMarks || !formData.maxMarks) {
        // alert('Please enter both Min Marks and Max Marks for Independent mode.');
        setSnackbarMessage('Please enter both Min Marks and Max Marks for Independent mode.');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning")
        return;
      }
      if (parseFloat(formData.maxMarks) <= parseFloat(formData.minMarks)) {
        // alert('Max Marks must be greater than Min Marks.');
        setSnackbarMessage('Max Marks must be greater than Min Marks.');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning")
        return;
      }
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      if (!apiUrl || !token) {
        // alert('API URL or token is missing.');
        setSnackbarMessage('API URL or token is missing.');
        setOpenSnackbar(true);
        setSnackbarSeverity("warning")
        return;
      }
      const payload = {
        ...formData,
        minMarks: formData.isExamModeIndependent ? parseFloat(formData.minMarks) || 0 : 0,
        maxMarks: formData.isExamModeIndependent ? parseFloat(formData.maxMarks) || 0 : 0
      };
      const response = await fetch(`${apiUrl}/ExaminationCriteria`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        // alert('Data saved successfully');
        setSnackbarMessage('Data saved successfully');
        setOpenSnackbar(true);
        setSnackbarSeverity("success")
        setFormData({
          classTypeId: '',
          examType: '',
          isExamModeIndependent: true,
          isExamWeightIndependent: true,
          minMarks: '',
          maxMarks: ''
        });
        fetchExam();
      } else {
        let errorMessage = 'Failed to save data';
        try {
          const errorData = await response.text();
          errorMessage += `: ${errorData || response.statusText || 'Unknown error'}`;
        } catch (textError) {
          console.error('Error reading response:', textError);
        }
        if (response.status === 401) {
          // alert('Unauthorized: Invalid or expired token. Please log in again.');
          setSnackbarMessage('Unauthorized: Invalid or expired token. Please log in again.');
          setOpenSnackbar(true);
          setSnackbarSeverity("error")
          navigate('/login');
        } else {
          // alert(errorMessage);
          setSnackbarMessage(errorMessage);
          setOpenSnackbar(true);
          setSnackbarSeverity("error")
        }
      }
    } catch (error) {
      // alert('API request error. Please check the console for details.');
      setSnackbarMessage('API request error. Please check the console for details.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
      console.error('API request error:', error);
    }
  };

  const handleReset = () => {
    setFormData({
      classTypeId: "",
      examType: "",
      isExamModeIndependent: true,
      isExamWeightIndependent: true,
      minMarks: "",
      maxMarks: ""
    });
  };

  const fetchExam = async (examCategoryId = 0) => {
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem("token");
      const response = await fetch(`${apiUrl}/ExaminationCriteria/GetExaminationCriteria`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({ examCategoryId }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.status === null && data.msg === "Record Not Found") {
          throw new Error("Record Not Found");
        }
        if (examCategoryId === 0) {
          setExamData(data);
        } else {
          setEditData(data);
          setFormData({
            classTypeId: data.classTypeId,
            examType: data.examType,
            isExamModeIndependent: data.isExamModeIndependent,
            isExamWeightIndependent: data.isExamWeightIndependent,
            minMarks: data.minNumber,
            maxMarks: data.maxNumber,
          });
        }
      } else {
        console.error("Failed to fetch exam data");
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      // alert("You don’t have permission to delete this exam.");
      setSnackbarMessage('You don’t have permission to delete this exam.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
      return;
    }
    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${apiUrl}/ExaminationCriteria/Id?Id=${selectedExam.examCriteriaId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });
      if (response.ok) {
        // alert("Exam deleted successfully");
        fetchExam();
        handleClose();
      } else {
        console.error('Delete failed');
        // alert('Failed to delete exam');
        setSnackbarMessage('Failed to delete exam');
        setOpenSnackbar(true);
        setSnackbarSeverity("error")
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred. Please try again later.');
      setSnackbarMessage('An error occurred. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
    }
  };

  const handleShow = (exam) => {
    setSelectedExam(exam);
    setShowModal(true);
  };

  const handleClose = () => {
    setSelectedExam(null);
    setShowModal(false);
  };

  const handleEdit = (exam) => {
    if (!canEdit) {
      // alert("You don’t have permission to edit this exam.");
      setSnackbarMessage('You don’t have permission to edit this exam.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error")
      return;
    }
    navigate(`/updateexamcriteria/${exam.examCriteriaId}`);
  };

  const handleCloseEditModal = () => {
    setEditData(null);
    setEditModal(false);
  };

  const handleMarksChange = (event) => {
    const { name, value } = event.target;
    if (value === '' || (/^\d+$/.test(value) && parseInt(value) >= 1 && parseInt(value) <= 100)) {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  useEffect(() => {
    fetchClassddl();
    fetchExam();
    Authorizer();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1); // reset to page 1
  };

  useEffect(() => {
    setFilteredData(examData);
  }, [examData]);


  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 24px', boxShadow: 3, borderRadius: '4px 4px 0 0', marginBottom: '12px', width: "100%" }}>
        <Typography variant="h4" sx={{ color: '#fff', textAlign: 'center', fontWeight: 'bold' }}>
          Exam Category <InfoIcon onClick={() => setShowUserGuide(true)} style={{ opacity: "0.6" }} />
        </Typography>
      </Box>
      <Dialog
        open={showUserGuide}
        onClose={() => setShowUserGuide(false)}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
            backgroundColor: "#f9fafb",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
            color: "white",
            fontWeight: "bold",
            textAlign: "center",
            borderRadius: "12px 12px 0 0",
            py: 2,
          }}
        >
          Examination Criteria User Guide
        </DialogTitle>
        <DialogContent sx={{ padding: "24px" }}>
          <Typography
            variant="h6"
            sx={{
              color: "#1898c2",
              fontWeight: "600",
              mb: 2,
              mt: 1,
              textAlign: "center",
            }}
          >
            Quick Steps to Add Exam
          </Typography>
          <Box
            component="ul"
            sx={{
              listStyleType: "none",
              padding: 0,
              "& li": {
                display: "flex",
                alignItems: "flex-start",
                mb: 1.5,
                fontSize: "1.0rem",
                color: "#333",
              },
              "& li::before": {
                content: '"\\2022"',
                color: "#1898c2",
                fontWeight: "bold",
                display: "inline-block",
                width: "1.5em",
                mr: 1,
                fontSize: "1.2rem",
              },
              "& strong": {
                margin: "0 0.2em",
              },
              "& em": {
                margin: "0 0.2em",
              },
            }}
          >
            <li>Select a <strong>Class Type</strong> from the dropdown menu.</li>
            <li>Enter the <strong>Exam Type</strong> in the text field.</li>
            <li>Choose the <strong>Exam Mode</strong> by selecting either <em>Independent</em> or <em>Subcategory</em>.</li>
            <li>Select the <strong>Exam Weightage</strong> by choosing either <em>Independent</em> or <em>Weightage</em>.</li>
            <li>If <em>Independent</em> mode is selected, enter the <strong>Min Marks</strong> and <strong>Max Marks</strong> (values between 1 and 100).</li>
            <li>Click the <strong>Save</strong> button to store the exam details.</li>
            <li>To reset the form, click the <strong>Reset</strong> button.</li>
            <li>View the list of exams in the table below the form.</li>
            <li>To edit an exam, click the <strong>Edit</strong> button next to the exam entry in the table.</li>
            <li>To delete an exam, click the <strong>Delete</strong> button next to the exam entry in the table and confirm the deletion.</li>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <StyledButton
            onClick={() => setShowUserGuide(false)}
            color="primary"
            variant="contained"
            sx={{
              backgroundColor: "#1898c2",
              "&:hover": { backgroundColor: "#156f9a" },
              borderRadius: "8px",
              px: 4,
            }}
          >
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>
      <Paper elevation={3} style={{ padding: 20, marginBottom: 20 }}>
        <form className="row g-4" style={{ marginTop: 20 }} onSubmit={handleSubmit}>
          <div className="col-md-5">
            <FormControl fullWidth>
              <InputLabel htmlFor="classType">Class Type</InputLabel>
              <Select
                id="classType"
                name="classTypeId"
                label="Class Type"
                value={formData.classTypeId}
                onChange={handleChange}
              >
                <MenuItem value="">--Select--</MenuItem>
                {className.map((item) => (
                  <MenuItem key={item.classTypeId} value={item.classTypeId}>
                    {item.classType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div className="col-md-5">
            <TextField
              fullWidth
              label="Exam Type"
              name="examType"
              value={formData.examType}
              onChange={handleChange}
            />
          </div>
          <div className="col-md-6">
            <FormControl component="fieldset">
              <Typography variant="subtitle1">Exam Mode</Typography>
              <RadioGroup row value={formData.isExamModeIndependent.toString()} onChange={handleRadioChange} name="isExamModeIndependent">
                <FormControlLabel value="true" control={<Radio />} label="Independent" />
                <FormControlLabel value="false" control={<Radio />} label="Subcategory" />
              </RadioGroup>
            </FormControl>
          </div>
          <div className="col-md-4">
            <FormControl component="fieldset">
              <Typography variant="subtitle1">Exam Weightage</Typography>
              <RadioGroup row value={formData.isExamWeightIndependent.toString()} onChange={handleRadioChange} name="isExamWeightIndependent">
                <FormControlLabel value="true" control={<Radio />} label="Independent" />
                <FormControlLabel value="false" control={<Radio />} label="Weightage" />
              </RadioGroup>
            </FormControl>
          </div>
          <div className="col-md-5">
            <TextField
              fullWidth
              label="Min Marks"
              name="minMarks"
              type="number"
              value={formData.minMarks}
              onChange={handleMarksChange}
              style={{ display: formData.isExamModeIndependent ? "block" : "none" }}
              required={formData.isExamModeIndependent}
              inputProps={{ min: 1, max: 100, step: 1 }}
              onKeyDown={(e) => { if (['-', 'e', '+', '.'].includes(e.key)) { e.preventDefault(); } }}
            />
          </div>
          <div className="col-md-5">
            <TextField
              fullWidth
              label="Max Marks"
              name="maxMarks"
              type="number"
              value={formData.maxMarks}
              onChange={handleMarksChange}
              style={{ display: formData.isExamModeIndependent ? "block" : "none" }}
              required={formData.isExamModeIndependent}
              inputProps={{ min: 1, max: 100, step: 1 }}
              onKeyDown={(e) => { if (['-', 'e', '+', '.'].includes(e.key)) { e.preventDefault(); } }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: 10 }}>
            <Tooltip title={canSave ? "Save Category" : "Not authorized to save"} arrow>
              <span>
                <Button variant="contained" color="primary" disabled={!canSave} type="submit" style={{ marginRight: 6 }}>
                  <b>Save</b>
                </Button></span>
            </Tooltip>
            <Button variant="contained" color="warning" onClick={handleReset} type="reset">
              <b>Reset</b>
            </Button>
          </div>
        </form>
      </Paper>
      <Paper elevation={3} style={{ padding: 20, marginBottom: 20 }}>
        <TableContainer component={Paper} style={{ marginTop: "16px" }}>
          <Table sx={{
            minWidth: 650,
            border: '1px solid #ccc',
            '& .MuiTableCell-root': {
              borderRight: '1px solid #ccc',
              fontSize: '1rem',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
            },
          }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  <strong>Serial No</strong>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  <strong>Class Type</strong>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  <strong>Exam Type</strong>
                </TableCell>
                <TableCell sx={{ borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  <strong>Exam Mode</strong>
                </TableCell>
                <TableCell sx={{ borderBottom: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem' }}>
                  <strong>Action</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((exam, index) => (
                <TableRow key={index} sx={{ borderTop: '1px solid #ccc' }}>
                  <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                    {index + 1}
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                    {exam.classType}
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                    {exam.examType}
                  </TableCell>
                  <TableCell sx={{ borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                    {exam.examMode}
                  </TableCell>
                  <TableCell sx={{ textAlign: 'center', borderRight: '1px solid #ccc', fontSize: '1.1rem' }}>
                    <Tooltip title={canEdit ? "Edit Exam" : "Not authorized to edit"} arrow>
                      <span>
                        <Button
                          className="mx-2"
                          variant="contained"
                          color="warning"
                          onClick={() => handleEdit(exam)}
                          disabled={!canEdit}
                          sx={{ marginRight: 1 }}
                          startIcon={<EditIcon />}
                        >
                          Edit
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={canDelete ? "Delete Exam" : "Not authorized to delete"} arrow>
                      <span>
                        <Button
                          variant="contained"
                          color="error"
                          onClick={() => handleShow(exam)}
                          disabled={!canDelete}
                          sx={{ marginRight: 1 }}
                          startIcon={<DeleteIcon />}
                        >
                          <b>DELETE</b>
                        </Button>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {filteredData.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
            <TextField
              select
              label="Rows per page"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              sx={{ width: 150 }}
              SelectProps={{
                native: true,
              }}
            >
              {rowsPerPageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </TextField>

            <Box>
              <Typography variant="body2" sx={{ display: 'inline-block', marginRight: 2 }}>
                Page {page} of {Math.ceil(filteredData.length / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page - 1)}
                disabled={page === 1}
                sx={{ mr: 1 }}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                onClick={() => handleChangePage(null, page + 1)}
                disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              >
                Next
              </Button>
            </Box>
          </Box>
        )}


      </Paper>
      <Modal open={editModal} onClose={handleCloseEditModal}>
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 800, padding: '20px', backgroundColor: '#fff', borderRadius: '8px' }}>
          <Typography variant="h6">Edit Exam</Typography>
          <form className="row g-4" style={{ marginTop: 20 }} onSubmit={handleSubmit}>
            <div className="col-md-5">
              <FormControl fullWidth>
                <InputLabel htmlFor="classType">Class Type</InputLabel>
                <Select
                  id="classType"
                  name="classTypeId"
                  label="Class Type"
                  value={formData.classTypeId}
                  onChange={handleChange}
                >
                  <MenuItem value="">--Select--</MenuItem>
                  {className.map((item) => (
                    <MenuItem key={item.classTypeId} value={item.classTypeId}>
                      {item.classType}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
            <div className="col-md-5">
              <TextField
                fullWidth
                label="Exam Type"
                name="examType"
                value={formData.examType}
                onChange={handleChange}
              />
            </div>
            <div className="col-md-6">
              <FormControl component="fieldset">
                <Typography variant="subtitle1">Exam Mode</Typography>
                <RadioGroup
                  row
                  value={(formData.isExamModeIndependent ?? true).toString()}
                  onChange={handleRadioChange}
                  name="isExamModeIndependent"
                >
                  <FormControlLabel value="true" control={<Radio />} label="Independent" />
                  <FormControlLabel value="false" control={<Radio />} label="Subcategory" />
                </RadioGroup>
                <RadioGroup
                  row
                  value={(formData.isExamWeightIndependent ?? true).toString()}
                  onChange={handleRadioChange}
                  name="isExamWeightIndependent"
                >
                  <FormControlLabel value="true" control={<Radio />} label="Independent" />
                  <FormControlLabel value="false" control={<Radio />} label="Weightage" />
                </RadioGroup>
              </FormControl>
            </div>
            <div className="col-md-5">
              <TextField
                fullWidth
                label="Min Marks"
                name="minMarks"
                type="number"
                value={formData.minMarks}
                onChange={handleChange}
                style={{ display: formData.isExamModeIndependent ? "block" : "none" }}
              />
            </div>
            <div className="col-md-5">
              <TextField
                fullWidth
                label="Max Marks"
                name="maxMarks"
                type="number"
                value={formData.maxMarks}
                onChange={handleChange}
                style={{ display: formData.isExamModeIndependent ? "block" : "none" }}
              />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
              <Button variant="contained" color="primary" type="submit">
                Save Changes
              </Button>
              <Button variant="contained" color="error" onClick={handleCloseEditModal}>
                Close
              </Button>
            </div>
          </form>
        </div>
      </Modal>


      <Modal open={showModal} onClose={handleClose} aria-labelledby="delete-modal-title" aria-describedby="delete-modal-description">
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you want to delete this Exam
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleClose} variant="outlined" sx={{ mr: 1 }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={() => handleDelete(handleDelete)}
              >
                Yes, Delete
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Snackbar
  open={openSnackbar}
  autoHideDuration={4000}
  onClose={() => setOpenSnackbar(false)}
  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
>
  <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
    {snackbarMessage}
  </Alert>
</Snackbar>

    </Container>
  );
}

export default ExamCategory;
